import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
// import AdSenseAd from '../components/ads/AdSenseAd'; // Removed to prevent AdSense violations
import { formatDate } from '../utils/helpers';
import { parseSocietyUrl, getSlugFromParams } from '../utils/urlHelpers';
import clubService from '../services/clubService';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';
import ListingCoverHero from '../components/common/ListingCoverHero';
import { getListingCoverImageUrl } from '../utils/imageFallback';
import ClickableImageGrid from '../components/common/ClickableImageGrid';
import useImageLightbox from '../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../utils/imageLightbox';
import { canManageOwnedResource } from '../utils/resourcePermissions';

const ClubDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const clubId = params.id;
  const { isSocietySpecific, societySlug } = parseSocietyUrl(location.pathname);
  const [club, setClub] = useState(null);
  const [relatedClubs, setRelatedClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const { openLightbox, Lightbox } = useImageLightbox();

  useEffect(() => {
    if (clubId) {
      loadClub();
      loadRelatedClubs();
    }
  }, [clubId]);

  const loadClub = async () => {
    try {
      setLoading(true);
      const data = await clubService.getClub(clubId);
      setClub(data);
      setViews(data.views || 0);
    } catch (err) {
      console.error('Error loading club:', err);
      setError('Failed to load club details');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedClubs = async () => {
    try {
      const data = await clubService.getRelatedClubs(clubId, {
        society: club?.society?.id,
        category: club?.category
      });
      setRelatedClubs(data.results || data);
    } catch (err) {
      console.error('Error loading related clubs:', err);
    }
  };

  const handleDelete = async () => {
    if (!isAuthenticated || !user || club?.user?.id !== user.id) {
      return;
    }

    if (window.confirm('Are you sure you want to delete this club?')) {
      try {
        await clubService.deleteClub(clubId);
        navigate('/clubs');
      } catch (err) {
        console.error('Error deleting club:', err);
        alert('Failed to delete club');
      }
    }
  };

  const handleEdit = () => {
    if (!isAuthenticated || !user || club?.user?.id !== user.id) {
      return;
    }
    navigate(`/clubs/${clubId}/edit`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSendingMessage(true);
    try {
      // TODO: Implement message sending
      console.log('Sending message:', message);
      setMessage('');
      setShowMessageForm(false);
      alert('Message sent successfully!');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      sports: 'bg-red-100 text-red-800',
      arts: 'bg-purple-100 text-purple-800',
      music: 'bg-pink-100 text-pink-800',
      dance: 'bg-orange-100 text-orange-800',
      literature: 'bg-indigo-100 text-indigo-800',
      photography: 'bg-yellow-100 text-yellow-800',
      cooking: 'bg-green-100 text-green-800',
      gardening: 'bg-emerald-100 text-emerald-800',
      technology: 'bg-blue-100 text-blue-800',
      gaming: 'bg-violet-100 text-violet-800',
      fitness: 'bg-rose-100 text-rose-800',
      yoga: 'bg-teal-100 text-teal-800',
      meditation: 'bg-cyan-100 text-cyan-800',
      book_club: 'bg-amber-100 text-amber-800',
      movie_club: 'bg-lime-100 text-lime-800',
      travel: 'bg-sky-100 text-sky-800',
      volunteer: 'bg-emerald-100 text-emerald-800',
      religious: 'bg-green-100 text-green-800',
      educational: 'bg-blue-100 text-blue-800',
      business: 'bg-gray-100 text-gray-800',
      other: 'bg-slate-100 text-slate-800'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading club details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !club) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Club Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The club you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/clubs')}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Clubs
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canManage = isAuthenticated && user && club && canManageOwnedResource(user, club);

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
                                <ListingCoverHero
                  src={getListingCoverImageUrl(club)}
                  alt={club.name}
                  onClick={
                    club.images?.length
                      ? () => openLightbox(normalizeGalleryImages(club.images, club.name), 0)
                      : undefined
                  }
                />
                <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h1 className="text-3xl font-bold text-gray-900">{club.name}</h1>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(club.category)}`}>
                        {club.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{club.society?.name || 'Unknown Society'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>{views} views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Posted {formatDate(club.created_at)}</span>
                      </div>
                    </div>

                    <GoogleMapsLocationLink
                      latitude={club.latitude}
                      longitude={club.longitude}
                      address={club.address}
                      name={club.name}
                      label="View on Map"
                      className="mb-4"
                    />
                  </div>

                  {/* Owner Actions */}
                  {canManage && (
                    <div className="flex gap-2 mt-4 sm:mt-0">
                      <button
                        onClick={handleEdit}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={handleDelete}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                </div>
              </div>

                            <ClickableImageGrid
                images={club.images}
                title="Images"
                namePrefix={club.name}
              />
              {Lightbox}

              {/* Description */}
              <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    {club.description || 'No description available.'}
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              {!canManage && isAuthenticated && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Club</h2>
                  {!showMessageForm ? (
                    <button
                      onClick={() => setShowMessageForm(true)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Send Message
                    </button>
                  ) : (
                    <form onSubmit={handleSendMessage} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message
                        </label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Type your message here..."
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={sendingMessage}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                        >
                          {sendingMessage ? 'Sending...' : 'Send Message'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowMessageForm(false)}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Related Clubs */}
              {relatedClubs.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Clubs</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedClubs.map((relatedClub) => (
                      <div
                        key={relatedClub.id}
                        onClick={() => navigate(`/clubs/${relatedClub.id}`)}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      >
                        <h3 className="font-semibold text-gray-900 mb-2">{relatedClub.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(relatedClub.category)}`}>
                            {relatedClub.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </p>
                        <p className="text-sm text-gray-500">{relatedClub.society?.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Removed ads to avoid navigation confusion */}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ClubDetailPage;
